<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <div v-if="!hideTitle" class="w-100 d-flex justify-content-between align-items-start mb-5">
            <div class="mb-0">
                <h3 class="mb-0">Supplier Document Hub</h3>
                <p v-if="canEdit" class="mt-3 mb-0">
                    Manage the Documents and other resources available to your Suppliers in the Supplier Document Hub.
                </p>
            </div>
            
            <div v-if="canEdit" class="d-flex gap-4">
                <b-button 
                    v-if="portalDocHubUrl"
                    variant="minimal-secondary" 
                    size="lg"
                    :href="portalDocHubUrl"
                    target="_blank"
                >
                    Preview
                    <b-icon icon="box-arrow-up-right"></b-icon>
                </b-button>
                <b-button
                    variant="success"
                    size="lg"
                    @click="startCreateResource"
                >
                    New Item
                </b-button>
            </div>
        </div>
        
        <!-- <div class="d-flex flex-wrap"> -->
        <transition name="fade-fast" mode="out-in">
            <div v-if="!initialLoadComplete || loading" key="loading" class="w-100" style="min-height: 400px; height: 400px;">
                <busy-spinner text="Loading..." class="h-100 w-100"></busy-spinner>
            </div>
            <div
                v-else 
                key="notLoading"
                class=""
            >
                <div class="row">
                    <!-- <div class="col-2">
                        <div class="sticky-top" style="top: 40px;">
                            <b-nav vertical pills v-b-scrollspy.dochub-groups-content>
                                <b-nav-item
                                    v-for="group in groupsWithResources"
                                    :key="group.uniqueId"
                                    :href="'#' + group.uniqueId"
                                >
                                    {{ group.name }}
                                </b-nav-item>
                            </b-nav>
                        </div>
                    </div> -->
                    <div id="dochub-groups-content" class="col" style="height: auto; position: relative;">
                        <div 
                            v-for="group in groupsWithResources"
                            :key="group.id"
                            :id="group.uniqueId"
                            class="mb-4 bg-gray-200 border rounded-lg overflow-hidden"
                        >
                            <b-button block v-b-toggle="`dochub-group-collapse-${group.id}`" variant="zone-dark" class="px-3 py-3 rounded-0 dochub-group-collapse-button">
                                <div class="d-flex align-items-end justify-content-between">
                                    <div class="d-flex align-items-center justify-content-start">
                                        <div class="dochub-group-collapse-icons mr-4 ml-2">
                                            <b-icon icon="plus-circle" class="when-collapsed" font-scale="2" variant="secondary"></b-icon>
                                            <b-icon icon="dash-circle" class="when-not-collapsed" font-scale="2" variant="secondary"></b-icon>
                                        </div>
                                        <div class="text-left">
                                            <h3 class="mb-0 text-primary-darker">{{ group.name }}</h3>
                                            <p v-if="group.description" class="text-muted mb-0 mt-2">{{ group.description }}</p>
                                        </div>
                                    </div>
                                    <b-button v-if="canEdit" variant="subtle-success" @click="createResourceInGroup(group)">
                                        + Add Item
                                    </b-button>
                                    <div v-else class="align-self-center font-size-100">{{ group.resources.length }} items</div>
                                </div>
                            </b-button>
                            <!-- <hr class="bg-gray-400"> -->
                            <b-collapse
                                :id="`dochub-group-collapse-${group.id}`"
                                visible
                            >
                                <div class="py-3 px-3 border-top" :class="group.wrapperClasses">
                                    <doc-hub-resource-item
                                        v-for="resource in group.resources"
                                        :key="resource.id"
                                        :resource="resource"
                                        :allowEdit="allowEdit"
                                        class=""
                                        :class="group.itemsClasses"
                                        :layoutType="group.layout_type"
                                        @updated="handleResourceUpdated"
                                        @request-update="startUpdateResource"
                                    ></doc-hub-resource-item>
                                </div>
                            </b-collapse>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <div v-if="noDocsAvailable" class="w-100 d-flex justify-content-center align-items-center" style="min-height: 400px; height: 400px;">
            <div class="font-weight-bold font-size-100 text-muted">
                {{ canEdit ? 'No Documents' : 'No Documents Available' }}
            </div>
        </div>
        <!-- </div> -->
        <!-- <div>
            <pre>{{ resourceList }}</pre>
        </div> -->

        <b-modal
            v-if="canEdit"
            v-model="editFormShowing"
            :title="isUpdate ? 'Update this item' : 'Create a new item'"
            size="md"
            hide-footer
            centered
            @hidden="onCloseEditModal"
        >
            <doc-hub-resource-form
                :existingData="resourceEditData"
                @cancel="onEditCancel"
                @saved="onEditSaved"
            ></doc-hub-resource-form>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin, splUrlMapMixin } from '../../js/custom-vue-mixins';
import DocHubResourceItem from './DocHubResourceItem.vue';
//import DocHubResourceVersionForm from./DocHubVersionForm.vueue';
import DocHubResourceForm from './DocHubResourceForm.vue';
import cloneDeep from 'lodash/cloneDeep';

// OLD - remove after moving to new DocHub stuff


export default {
    name: 'DocHubResourceList',
    components: {
        DocHubResourceItem,
        DocHubResourceForm,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        splUrlMapMixin,
    ],
    props: {
        allowEdit: {  // must set this true if edit is allowed at all (and it only shows edit stuff if user can do it)
            type: Boolean,
            default: false,
        },
        hideTitle: {
            type: Boolean,
            default: false,
        },
        onlyPublished: {  // if you want to FORCE to only show PUBLISHED docs, set this -- like for portal preview
            // backend will still never show non-published things to people who should not see them
            // this is only for limiting to only published for people who can otherwise see all
            type: Boolean,
            default: false,
        },
        layoutType: {
            type: String,
            default: 'GRID',  // options are from backend enum: GRID, LIST - this controls things NOT in groups, groups have their own layout
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            initialLoadComplete: false,
            resourceList: [],  // list of DocHubResources from API
            resourcesById: {},  // map of resource_id to resource
            resourcesByGroupId: {},  // map of group_id to list of resources
            resourceGroups: [],  // list of DocHubResourceGroups from API
            editFormShowing: false,
            isUpdate: false,  // change from different methods
            resourceEditData: null, // set it depending on update/create
            noGroupData: {
                id: 'NO_GROUP',  // this should match the backend.... data will come from API with this key if no group
                name: 'Other Items',
                description: '',
                sort_order: 0,
                layout_type: 'FULL_CARDS',
            },
        }
    },
    computed: {
        canEdit() {
            return this.allowEdit && this.$currentUserInfo.is_tenant_admin;
        },
        portalDocHubUrl() {
            return this.$splUrlMap['portal_dochub'] || '';
        },
        noDocsAvailable() {
            return Object.values(this.resourcesById).length < 1;
            //return !this.loading && this.initialLoadComplete && Array.isArray(this.resourceList) && (this.resourceList.length < 1);
        },
        groupsWithResources() {
            // return ARRAY of groups, in the order returned from API, with nested resources, ordered as returned from API also (we do the ordering there)
            const groups = this.resourceGroups.map(g => this.makeLocalGroup(g));
            if (this.noGroupData.id in this.resourcesByGroupId) {
                // put it first or last?
                groups.push(this.makeLocalGroup(this.noGroupData));
            }
            return groups;
        },
        resourceGroupOptions() {
            // return array of objects with value and text for use in a select
            return this.resourceGroups.map(g => ({value: g.id, text: g.name}));
        },
        hasMultipleGroups() {
            return this.groupsWithResources.length > 1;
        },
        showSideNav() {
            return this.hasMultipleGroups && !this.canEdit;
        },
    },
    mounted() {
        //this.getList();
        this.getResourcesAndGroups();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getList() {
            this.loading = true;
            try {
                let axiosConfig = {};
                if (this.onlyPublished) {
                    axiosConfig['params'] = {only_published: true};
                }
                const resp = await suppliosApi.dochubResource.list(axiosConfig);
                this.resourceList = resp.data;  // not paginated (yet)
                this.initialLoadComplete = true;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        async refresh() {
            await this.getResourcesAndGroups();
        },
        async getResourcesAndGroups() {
            this.loading = true;
            let resp;
            const onlyPublished = this.onlyPublished;
            try {
                resp = await suppliosApi.dochubResource.list_resources_and_groups(onlyPublished);
                this.resourcesByGroupId = resp.data.resources_by_group_id; 
                this.resourcesById = Object.fromEntries(Object.values(this.resourcesByGroupId).flat().map(r => [r.id, r]));
                this.resourceGroups = resp.data.groups;
                this.initialLoadComplete = true;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        handleResourceUpdated() {
            this.refresh();
            //this.getList();
            // crude, but works for now
        },
        startCreateResource() {
            this.resourceEditData = {};
            this.isUpdate = false;
            this.editFormShowing = true;
        },
        createResourceInGroup(groupData) {
            const realGroupId = groupData.id === this.noGroupData.id ? null : groupData.id;
            this.resourceEditData = {group: realGroupId};
            this.isUpdate = false;
            this.editFormShowing = true;
        },
        startUpdateResource(resourceId) {  // emitted from child component
            const resourceData = this.resourceList.find(r => r.id === resourceId);
            if (!resourceData) {
                return;
            }
            this.resourceEditData = cloneDeep(resourceData);
            this.isUpdate = true;
            this.editFormShowing = true;
        },
        onEditCancel() {
            this.onCloseEditModal();
            // no need to refresh from parent here
        },
        onEditSaved() {
            this.onCloseEditModal();
            this.refresh();
            //this.getList();
        },
        onCloseEditModal() {
            // RESET THINGS
            this.editFormShowing = false;
            this.isUpdate = false;
            this.resourceEditData = null;
        },
        makeLocalGroup(groupData) {
            // make our local version of the groups, with resources attached
            return {
                ...groupData,
                uniqueId: `dochubgroup-${groupData.id}`,
                resources: this.resourcesByGroupId[groupData.id],
                wrapperClasses: this.getItemsWrapperClasses(groupData),
                itemsClasses: this.getItemsClasses(groupData),
            };
        },
        getItemsWrapperClasses(groupData) {
            const defaultClasses = ['w-100'];
            const defaultCardClasses = ['d-flex', 'flex-wrap', 'justify-content-center', 'align-items-start'];
            const smallCardsClasses = ['gap-6'];
            const largeCardsClasses = ['gap-7'];
            const fullCardsClasses = ['gap-4'];
            const listClasses = ['list-group', 'rounded-lg'];
            switch (groupData.layout_type) {
                case 'LIST':
                    return [...defaultClasses, ...listClasses];
                case 'GRID':
                    return [...defaultClasses, ...defaultCardClasses, ...smallCardsClasses];
                case 'LARGE_CARDS':
                    return [...defaultClasses, ...defaultCardClasses, ...largeCardsClasses];
                case 'SMALL_CARDS':
                    return [...defaultClasses, ...defaultCardClasses, ...smallCardsClasses];
                case 'FULL_CARDS':
                    return [...defaultClasses, ...defaultCardClasses, ...fullCardsClasses];
                default:
                    return [...defaultClasses];
            }
        },
        getItemsClasses(groupData) {
            const defaultClasses = ['bg-gray-100', 'overflow-hidden'];
            const defaultCardClasses = ['border', 'rounded-lg', 'shadow-sm'];  // 'col', 
            const smallCardsClasses = ['w-30'];
            const largeCardsClasses = ['w-45'];
            const fullCardsClasses = ['w-100'];
            const listClasses = ['list-group-item', 'p-0', 'border-gray-400'];
            switch (groupData.layout_type) {
                case 'LIST':
                    return [...defaultClasses, ...listClasses];
                case 'GRID':
                    return [...defaultClasses, ...defaultCardClasses, ...smallCardsClasses];
                case 'LARGE_CARDS':
                    return [...defaultClasses, ...defaultCardClasses, ...largeCardsClasses];
                case 'SMALL_CARDS':
                    return [...defaultClasses, ...defaultCardClasses, ...smallCardsClasses];
                case 'FULL_CARDS':
                    return [...defaultClasses, ...defaultCardClasses, ...fullCardsClasses];
                default:
                    return [...defaultClasses];
            }
        },
    }
}
</script>

<style lang="scss">

.dochub-group-collapse-button.collapsed .dochub-group-collapse-icons > .when-not-collapsed,
.dochub-group-collapse-button.not-collapsed .dochub-group-collapse-icons > .when-collapsed {
    display: none;
}

</style>